<template>
  <div class="event-modal-catalog-package">
    <h2 class="event-modal-catalog-package__title">
      {{ $t("event.catalog.modal.packageServices.logoPlacementTitle") }}
    </h2>

    <div class="event-modal-catalog-package__logo-placement">
      <Checkbox
        class="event-modal-catalog-package__logo-placement-checkbox"
        v-for="service in packagePlacementLogo"
        :key="service.id"
        :disabled="!hasAccessToWrite"
        :value="
          packagePlacement.selectedLogoPlacementIds.indexOf(service.id) > -1
        "
        @input="updatePackagePlacementLogo({ service, value: $event })"
      >
        {{ service.title }}

        <span
          :class="{
            _highlight:
              packagePlacement.selectedLogoPlacementIds.indexOf(service.id) > -1
          }"
        >
          <template v-if="service.errorPrice">
            <ErrorPriceCatalog />
          </template>
          <template v-else>{{ service.price }} {{ currencySymbol }}</template>
        </span>
      </Checkbox>
    </div>

    <div class="event-modal-catalog-package__logo-placement">
      <EventModalCatalogPackageLogo
        :isRequiredUpload="allowUploadLogo"
        fileType="logoFile"
        @fileChanged="logoChanged"
        @downloadFile="downloadLogo"
        @removeFile="removeLogo"
      >
        <template #title> {{ $t("event.catalog.modal.logo.title") }}</template>
        <template #description>
          {{ $t("event.catalog.modal.logo.description") }}</template
        >
      </EventModalCatalogPackageLogo>
      <EventModalCatalogPackageLogo
        :isRequiredUpload="allowUploadCoverFiles"
        fileType="coverImg"
        @fileChanged="coverChanged"
        @downloadFile="downloadCoverImg"
        @removeFile="removeCoverImg"
        file-accept=".jpg,.jpeg,.png"
      >
        <template #title> {{ $t("event.catalog.modal.cover.title") }}</template>
        <template #description>
          {{ $t("event.catalog.modal.cover.description") }}</template
        >
      </EventModalCatalogPackageLogo>
    </div>

    <div class="event-modal-catalog-package__logo-placement">
      <EventModalCatalogPackageLogo
        :isRequiredUpload="allowUploadCoverFiles"
        fileType="adModulePdf"
        fileAccept=".pdf"
        @fileChanged="presentationChanged"
        @downloadFile="downloadPresentation"
        @removeFile="removePresentation"
      >
        <template #title>
          {{ $t("event.catalog.modal.cover.presentationFileTitle") }}</template
        >
        <template #description>
          {{
            $t("event.catalog.modal.cover.presentationFileDescription")
          }}</template
        >
      </EventModalCatalogPackageLogo>

      <InputLabel>
        <h3 class="event-modal-catalog-package-logo__title">
          {{ $t("event.catalog.modal.cover.videoLinkTitle") }}
        </h3>
        <input
          v-model="adVideoFiles"
          :disabled="!allowUploadCoverFiles"
          :placeholder="$t('event.catalog.modal.cover.videoLinkPlaceholder')"
        />
      </InputLabel>
    </div>

    <div class="event-modal-catalog-package__graphic-files">
      <EventModalCatalogPackageLogo
        :isRequiredUpload="hasAccessToWrite"
        fileType="adGraphic"
        fileAccept=".jpg, .jpeg, .png, .zip"
        @fileChanged="graphicFilesChanged"
        @downloadFile="downloadGraphicFile"
        @removeFile="removeGraphicFile"
      >
        <template #title>
          {{ $t("event.catalog.modal.adServices.graphicFiles") }}</template
        >
        <template #description>
          {{ $t("event.catalog.modal.adServices.graphicFilesFormats") }}
        </template>
        <template #input>
          <InputLabel>
            <input
              v-model="graphicFiles"
              :disabled="!hasAccessToWrite"
              :placeholder="
                $t('event.catalog.modal.adServices.graphicFilesPrimary')
              "
            />
          </InputLabel>
        </template>
      </EventModalCatalogPackageLogo>
    </div>

    <h2 class="event-modal-catalog-package__title --with-margin">
      {{ $t("event.catalog.modal.packageServices.majorServicesTitle") }}
    </h2>

    <div class="event-modal-catalog-package__container">
      <div
        class="event-modal-catalog-package__item"
        v-for="item in packagePlacementMajorPackages"
        :key="item.id"
        :class="{
          _selected:
            packagePlacement.selectedMajorPackageIds.indexOf(item.id) > -1,
          _disabled: !hasAccessToWrite || isNotAllowedService(item)
        }"
      >
        <div class="event-modal-catalog-package__item-header">
          <div
            class="event-modal-catalog-package__item-header-title"
            @click="selectMajorService(item)"
          >
            <div>
              {{ item.title }}
              <span>
                <template v-if="item.errorPrice">
                  <ErrorPriceCatalog />
                </template>
                <template v-else>
                  {{ item.price }} {{ currencySymbol }}
                </template>
              </span>
            </div>
          </div>

          <EventModalCatalogExample
            class="event-modal-catalog-package__item-header-example"
            :images="item.images"
          >
            <template #label>
              Пример
            </template>
          </EventModalCatalogExample>
        </div>

        <div
          class="event-modal-catalog-package__item-1c-wrapper"
          v-html="item.servicesHTML"
        >
          <!-- should contain ul > li > { h3(service title) + p(service description) } -->
        </div>
      </div>
    </div>

    <h2 class="event-modal-catalog-package__title">
      {{ $t("event.catalog.modal.packageServices.additionalServicesTitle") }}
    </h2>

    <div class="event-modal-catalog-package__container">
      <div
        class="event-modal-catalog-package__item event-modal-catalog-package__item_xs"
        v-for="item in packagePlacementAdditionalPackages"
        :key="item.id"
        :class="{
          _selected:
            packagePlacement.selectedAdditionalPackageId.indexOf(item.id) > -1,
          _disabled: !hasAccessToWrite
        }"
      >
        <div class="event-modal-catalog-package__item-header">
          <div
            class="event-modal-catalog-package__item-header-title"
            @click="selectAdditionalService(item)"
          >
            {{ item.title }}
          </div>

          <EventModalCatalogExample
            class="event-modal-catalog-package__item-header-example"
            :images="item.images"
          >
            <template #label>
              Пример
            </template>
          </EventModalCatalogExample>
        </div>

        <div
          class="event-modal-catalog-package__item-1c-wrapper"
          v-html="item.servicesHTML"
        >
          <!-- should contain ul > li > { h3(service title) + p(service description) } -->
        </div>

        <div class="event-modal-catalog-package__item-footer">
          <template v-if="item.errorPrice">
            <ErrorPriceCatalog />
          </template>
          <template v-else>{{ item.price }} {{ currencySymbol }}</template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Checkbox, InputLabel } from "@v2/shared/ui/components";
import EventModalCatalogExample from "./EventModalCatalogExample.vue";
import EventModalCatalogPackageLogo from "./EventModalCatalogPackageLogo.vue";

import { mapState, mapGetters, mapActions, mapMutations } from "vuex";

import apiCatalog from "@/api/catalog";
import ErrorPriceCatalog from "@/components/Modal/Catalog/ErrorPriceCatalog.vue";

export default {
  name: "EventModalCatalogPackageForm",

  components: {
    ErrorPriceCatalog,
    Checkbox,
    InputLabel,
    EventModalCatalogExample,
    EventModalCatalogPackageLogo
  },

  computed: {
    ...mapGetters("event", ["hasAccessToWrite", "eventId"]),

    ...mapGetters("eventCatalog", [
      "packagePlacementMajorPackages",
      "packagePlacementAdditionalPackages",
      "packagePlacementLogo",
      "currencySymbol",
      "getOrderDataFromCatalog",
      "requiredLogoServices",
      "requiredAdModule",
      "allowUploadLogo",
      "allowUploadCoverFiles"
    ]),

    ...mapState("event", ["event"]),
    ...mapState("eventCatalog", [
      "packagePlacement",
      "adServices",
      "info",
      "files",
      "selectedPackagesId",
      "selectedServicesForRubrics"
    ]),

    graphicFiles: {
      get() {
        return this.$store.state.eventCatalog.adServices.graphicFilesLink || "";
      },

      set(value) {
        this.SET_PACKAGE_PLACEMENT_GRAPHIC_FILES_LINK(value);
      }
    },
    adVideoFiles: {
      get() {
        return this.$store.state.eventCatalog.adServices.adVideoFilesLink || "";
      },

      set(value) {
        this.SET_PACKAGE_PLACEMENT_VIDEO_LINK(value);
      }
    }
  },

  methods: {
    ...mapActions("eventCatalog", [
      "updatePackagePlacementMajorPackage",
      "updatePackagePlacementAdditionalPackage",
      "updatePackagePlacementLogo"
    ]),

    ...mapMutations("eventCatalog", [
      "SET_PACKAGE_PLACEMENT_GRAPHIC_FILES_LINK",
      "SET_PACKAGE_PLACEMENT_VIDEO_LINK"
    ]),
    ...mapMutations("event", ["UPDATE_CATALOG_STATUS"]),

    selectMajorService(item) {
      if (!this.hasAccessToWrite || this.isNotAllowedService(item)) {
        return;
      }

      this.updatePackagePlacementMajorPackage(item.id);
    },

    selectAdditionalService(item) {
      if (!this.hasAccessToWrite) {
        return;
      }

      this.updatePackagePlacementAdditionalPackage(item.id);
    },

    isNotAllowedService(item) {
      const selectedPackages = this.packagePlacement.selectedMajorPackageIds;

      if (!selectedPackages.length || selectedPackages.includes(item.id))
        return false;

      return !(
        selectedPackages.length <=
        this.info.allowed_services.filter(
          allowedService =>
            selectedPackages.filter(
              packageId =>
                packageId === allowedService.first_service_id &&
                allowedService.second_service_id === item.id
            ).length
        ).length +
          this.info.allowed_services.filter(
            allowedService =>
              selectedPackages.filter(
                packageId =>
                  packageId === allowedService.second_service_id &&
                  allowedService.first_service_id === item.id
              ).length
          ).length
      );
    },
    logoChanged(e) {
      this.$store.dispatch("eventCatalog/setFilePackage", {
        eventId: this.event.id,
        orderId: this.event.catalog_order_id,
        file: e.target.files[0],
        type: "logo"
      });
    },
    coverChanged(e) {
      this.$store.dispatch("eventCatalog/setFilePackage", {
        eventId: this.event.id,
        orderId: this.event.catalog_order_id,
        file: e.target.files[0],
        type: "company-cover"
      });
    },
    graphicFilesChanged(e) {
      this.$store.dispatch("eventCatalog/setFilePackage", {
        eventId: this.event.id,
        orderId: this.event.catalog_order_id,
        file: e.target.files[0],
        type: "graphics"
      });
    },
    presentationChanged(e) {
      this.$store.dispatch("eventCatalog/setFilePackage", {
        eventId: this.event.id,
        orderId: this.event.catalog_order_id,
        file: e.target.files[0],
        type: "presentation"
      });
    },
    async downloadLogo(e) {
      e.preventDefault();
      if (!this.files.logoFile) {
        return;
      }
      const response = await apiCatalog.downloadFile(
        this.event.id,
        this.event.catalog_order_id,
        "logo"
      );
      let blob = new Blob([response], {
        type: "application/pdf"
      });
      const url = window.URL.createObjectURL(blob);
      let link = document.createElement("a");
      link.href = url;
      link.download = this.files.logoFile.original_name;
      link.click();
    },
    async downloadCoverImg(e) {
      e.preventDefault();
      if (!this.files.coverImg) {
        return;
      }
      const response = await apiCatalog.downloadFile(
        this.event.id,
        this.event.catalog_order_id,
        "company-cover"
      );
      let blob = new Blob([response], {
        type: "application/pdf"
      });
      const url = window.URL.createObjectURL(blob);
      let link = document.createElement("a");
      link.href = url;
      link.download = this.files.coverImg.original_name;
      link.click();
    },
    async downloadGraphicFile(e) {
      e.preventDefault();
      if (!this.files.adGraphic) {
        return;
      }
      const response = await apiCatalog.downloadFile(
        this.event.id,
        this.event.catalog_order_id,
        "graphics"
      );
      let blob = new Blob([response], {
        type: "application/pdf"
      });
      const url = window.URL.createObjectURL(blob);
      let link = document.createElement("a");
      link.href = url;
      link.download = this.files.adGraphic.original_name;
      link.click();
    },
    async downloadPresentation(e) {
      e.preventDefault();
      if (!this.files.adModulePdf) {
        return;
      }
      const response = await apiCatalog.downloadFile(
        this.event.id,
        this.event.catalog_order_id,
        "presentation"
      );
      let blob = new Blob([response], {
        type: "application/pdf"
      });
      const url = window.URL.createObjectURL(blob);
      let link = document.createElement("a");
      link.href = url;
      link.download = this.files.adModulePdf.original_name;
      link.click();
    },
    async removeCoverImg() {
      if (!this.files.coverImg) {
        return;
      }
      await this.$store.dispatch("eventCatalog/removePackageFile", {
        eventId: this.event.id,
        orderId: this.event.catalog_order_id,
        fileId: this.files.coverImg.id,
        type: "company-cover"
      });
    },
    async removeGraphicFile() {
      if (!this.files.adGraphic) {
        return;
      }
      await this.$store.dispatch("eventCatalog/removePackageFile", {
        eventId: this.event.id,
        orderId: this.event.catalog_order_id,
        fileId: this.files.adGraphic.id,
        type: "graphics"
      });
    },
    async removePresentation() {
      if (!this.files.adModulePdf) {
        return;
      }
      await this.$store.dispatch("eventCatalog/removePackageFile", {
        eventId: this.event.id,
        orderId: this.event.catalog_order_id,
        fileId: this.files.adModulePdf.id,
        type: "presentation"
      });
    },
    async removeLogo() {
      if (!this.files.logoFile) {
        return;
      }
      await this.$store.dispatch("eventCatalog/removePackageFile", {
        eventId: this.event.id,
        orderId: this.event.catalog_order_id,
        fileId: this.files.logoFile.id,
        type: "logo"
      });
    }
  },

  beforeUpdate() {
    this.$store.dispatch(
      "eventCatalog/isRequiredUploadLogo",
      this.allowUploadLogo
    );
  },

  beforeDestroy() {
    this.UPDATE_CATALOG_STATUS({
      field: "has_started_order",
      value: true
    });

    apiCatalog.update(
      this.eventId,
      this.getOrderDataFromCatalog,
      this.event.catalog_order_id
    );
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.event-modal-catalog-package {
  &__title {
    @include h2();
    margin-bottom: 22px;

    @include respond-down(md) {
      font-size: 16px;
      margin-bottom: 16px;
    }

    &.--with-margin {
      margin-top: 55px;

      @include respond-down(md) {
        margin-top: 24px;
      }
    }
  }

  &__logo-placement {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 30px;
    margin-bottom: 40px;
    &-checkbox {
      span {
        font-weight: bold;
        color: #a3a8b3;

        display: block;
        margin-top: 10px;

        &._highlight {
          color: #0e6dbb;
        }
      }
    }
  }

  &__graphic-files {
    margin-top: 42px;

    @include respond-down(md) {
      margin-top: 30px;
    }
  }

  &__container {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  &__item {
    width: 380px;

    @include respond-down(lg) {
      width: 350px;
    }

    @include respond-down(md) {
      width: 100%;
    }

    margin-bottom: 23px;
    border-radius: 2px;
    background-color: #ffffff;
    border: 1px solid #f1f1f1;
    outline: 1px solid transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;

    transition: 0.2s border;

    &._selected {
      border-color: #0070d2;
      border-width: 2px;
      outline: none;
    }

    &-header {
      display: flex;
      align-items: flex-start;

      padding: 24px 32px 20px 32px;

      @include respond-down(md) {
        padding: 20px 24px 16px 24px;
      }

      border-bottom: 1px solid #f1f1f1;

      &-title {
        @include h3();
        display: flex;

        margin-right: auto;

        cursor: pointer;

        @include respond-down(md) {
          font-size: 14px;
        }

        span {
          display: block;
          font-size: 14px;
          margin-top: 8px;

          color: #a3a8b3;
        }

        &::before {
          content: "";

          width: 24px;
          height: 24px;

          border-radius: 50%;
          background-color: #ffffff;

          border: 1px solid #cdced0;

          box-sizing: border-box;

          margin-right: 12px;

          // cursor: pointer;
        }
      }

      &-example {
        margin-top: 5px;
      }
    }

    &_xs &-header {
      border-bottom: none;
      padding-bottom: 19px;

      &-example {
        margin-top: 3px;
      }
    }

    &._selected &-header {
      &-title {
        &::before {
          background-image: radial-gradient(
            #0070d2 0%,
            #0070d2 30%,
            transparent 30%
          );
        }
        span {
          color: #0070d2;
        }
      }
    }

    &._disabled {
      background-color: rgba(241, 241, 241, 0.5);
    }

    &._disabled &-header {
      &-title {
        cursor: default;

        &::before {
          background-color: rgba(241, 241, 241, 0.5);
        }
      }
    }

    &-list {
      padding: 23px 32px 20px 32px;

      @include respond-down(md) {
        padding: 20px 24px 16px 24px;
      }

      list-style: none;

      &-item {
        display: flex;
        margin-bottom: 21px;

        &-icon {
          width: 24px;
          height: 24px;

          flex-shrink: 0;

          margin-right: 12px;

          .svg-icon {
            width: 100%;
            height: 100%;
          }
        }

        &-title {
          font-size: 13px;
          font-weight: normal;
          line-height: 1.54;
          color: #000000;
          margin: 0 0 6px 0;
        }

        &-desc {
          font-size: 12px;
          line-height: 1.33;
          color: #6d7179;
          margin: 0;
        }
      }
    }

    &_xs &-list {
      padding-top: 0;
      margin-left: 35px;
      padding-bottom: 0;
      margin-bottom: auto;
    }

    &-footer {
      padding-top: 10px;
      padding-left: 30px;
      padding-bottom: 27px;

      @include respond-down(md) {
        padding-left: 25px;
      }

      font-size: 14px;
      font-weight: bold;
      line-height: 1.43;
      color: #a3a8b3;
    }

    &._selected &-footer {
      color: #0070d2;
    }
  }
}
</style>

<style lang="scss">
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.event-modal-catalog-package {
  &__graphic-files {
    textarea {
      width: 100%;
      height: 96px;
      padding: 10px 16px;
      border-radius: 2px;
      background-color: #ffffff;
      box-sizing: border-box;
      border: 1px solid #cdced0;
      resize: none;
      outline: none;

      @include respond-down(md) {
        height: 80px;
        padding-left: 12px;
        padding-right: 12px;
      }

      &:hover,
      &:focus {
        border-color: #a3a8b3;
      }

      &[disabled],
      &.disabled {
        background: rgba(241, 241, 241, 0.5);
        border-color: rgba(205, 206, 208, 0.5);

        pointer-events: none;
        touch-action: none;

        color: #000000;
      }

      &::placeholder {
        color: #cdced0;
      }

      &.error {
        border-color: #ea5330;
        color: #ea5330;
      }
    }
  }
}

.event-modal-catalog-package__item {
  &-1c-wrapper {
    padding: 23px 32px 20px 32px;

    @include respond-down(md) {
      padding: 20px 24px 16px 24px;
    }

    ul {
      margin: 0;
      padding: 0 0 0 25px;

      list-style: none;

      li::before {
        content: "";
        display: inline-block;

        width: 5px;
        height: 5px;

        transform: translate(-16px, 6px);

        background: #009ee3;
        border-radius: 50%;

        position: absolute;
      }

      li:not(:last-of-type) {
        margin-bottom: 10px;
      }

      h3 {
        font-size: 13px;
        font-weight: normal;
        line-height: 1.54;
        color: #000000;
        margin: 0 0 6px 0;
      }

      p {
        font-size: 12px;
        line-height: 1.33;
        color: #6d7179;
        margin: 0;
      }
    }
  }
}
</style>
